import React, { useCallback, useEffect, useState } from 'react';
import * as R from 'ramda';
import {
  SaveButton,
  useDataProvider,
  useNotify,
  useRedirect,
} from 'react-admin';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@material-ui/core';
import { CheckCircle, ErrorOutline } from '@material-ui/icons';
import { useFormState } from 'react-final-form';

const ConfirmDialog = (props) => {
  const { isOpen, onClose, onConfirm } = props;

  const handleConfirm = useCallback(
    (e) => {
      e.stopPropagation();
      onConfirm(e);
    },
    [onConfirm]
  );

  const handleClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      onClick={handleClick}
      aria-labelledby="alert-dialog-title"
    >
      <DialogTitle id="alert-dialog-title">Github Course ID Changed</DialogTitle>
      <DialogContent>
        <DialogContentText>
          This will completely erase all the progress and deliveries from all
          the current students in the cohort. Are you sure?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          <Box display="flex" pr="0.5em">
            <ErrorOutline />
          </Box>
          Cancel
        </Button>
        <Button onClick={handleConfirm} autoFocus>
          <Box display="flex" pr="0.5em">
            <CheckCircle />
          </Box>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CohortSaveButton = (props) => {
  const redirect = useRedirect();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [isConfirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [updates, setUpdates] = useState({});
  const [hasCourseIdUpdates, setHasCourseIdUpdates] = useState(false);
  const [isSaveAgreed, setSaveAgreed] = useState(false);

  const formState = useFormState();

  useEffect(() => {
    const save = () => {
      const cohortId = updates.cohort_id;
      const cohortSettingsPayload = updates.cohortSettingsPayload;
      const cohortData = R.omit(['cohort_id', 'cohortSettingsPayload'], updates);
      const hasCohortChanges = !R.isEmpty(cohortData);

      const cohortUpdate = hasCohortChanges
        ? dataProvider.update('cohorts', {
            id: cohortId,
            data: cohortData,
          })
        : Promise.resolve();

      cohortUpdate
        .then(() => {
          if (cohortSettingsPayload != null) {
            return dataProvider.update('cohort_settings', {
              id: cohortId,
              data: cohortSettingsPayload,
            });
          }
          return null;
        })
        .then(() => {
          redirect(`/cohorts/${cohortId}/show`);
          notify('Cohort Updated');
        })
        .catch((error) => {
          notify(`Cohort update error: ${error.message}`, 'warning');
        });
    };
    if (!R.isEmpty(updates) && isSaveAgreed) {
      save();
    }
  }, [dataProvider, isSaveAgreed, notify, redirect, updates]);

  useEffect(() => {
    if (hasCourseIdUpdates) {
      setConfirmDialogOpen(true);
    }
  }, [hasCourseIdUpdates]);

  const handleConfirm = useCallback(() => {
    setConfirmDialogOpen(false);
    setSaveAgreed(true);
  }, []);

  const handleClose = useCallback((e) => {
    setConfirmDialogOpen(false);
    setHasCourseIdUpdates(false);
    e.stopPropagation();
  }, []);

  const handleSave = useCallback(() => {
    const editUpdates = R.pipe(
      R.propOr({}, 'modified'),
      R.filter(R.identity),
      R.keys,
      R.map(
        R.pipe(R.replace(/\[|\]/g, '.'), R.split('.'), R.reject(R.isEmpty))
      ),
      R.reject((path) =>
        R.eqBy(R.path(path), formState.initialValues, formState.values)
      ),
      R.reduce(
        (acc, path) => R.assocPath(path, R.path(path, formState.values), acc),
        {}
      )
    )(formState);
    if (R.isEmpty(editUpdates)) {
      notify('No Changes Detected');
    } else {
      const cohortSettingsChanged = R.has('cohort_settings', editUpdates);
      const cohortSettingsPayload = cohortSettingsChanged
        ? formState.values.cohort_settings
        : undefined;
      const cohortOnlyUpdates = R.omit(['cohort_settings'], editUpdates);

      if (R.has('timetable', cohortOnlyUpdates)) {
        cohortOnlyUpdates.timetable = formState.values.timetable;
      }
      if (R.has('offerings', cohortOnlyUpdates)) {
        cohortOnlyUpdates.offerings = formState.values.offerings;
      }

      if (!R.isEmpty(cohortOnlyUpdates)) {
        cohortOnlyUpdates.external_ids = formState.values.external_ids;
      }
      cohortOnlyUpdates.cohort_id = formState.values.id;
      cohortOnlyUpdates.cohortSettingsPayload = cohortSettingsPayload;

      const courseIdsChanged = R.anyPass([
        R.hasPath(['prework', 'openedx']),
        R.hasPath(['course', 'openedx']),
        R.hasPath(['career_services', 'openedx']),
      ])(cohortOnlyUpdates);
      if (courseIdsChanged) {
        setHasCourseIdUpdates(true);
      } else {
        setSaveAgreed(true);
      }
      setUpdates(cohortOnlyUpdates);
    }
  }, [formState, notify]);
  return (
    <>
      <SaveButton {...props} onSave={handleSave} />
      <ConfirmDialog
        isOpen={isConfirmDialogOpen}
        onConfirm={handleConfirm}
        onClose={handleClose}
      />
    </>
  );
};

export default CohortSaveButton;
